import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { useFormikContext, getIn } from 'formik';
import { Grid } from '@material-ui/core';
import { FarmRiskType, lookupsJson } from '@ourbranch/lookups';

import { BrandFormField } from 'common/components/farm/brand-form-field';
import { useActiveFormState } from 'common/hooks/use-active-form-state';
import CollapsibleCard from 'core/components/collapsible-card';
import Field from 'core/components/form/form.v2';
import Label from 'core/components/label/label';
import { FarmIncomeCoverageForm } from '../../sub-risks';
import { useRouteHelper } from 'core/hooks/use-route-helper';

import useStyles from './FO6-unscheduled.styles';

const eqDeductibleOptions = lookupsJson.farm.earthquakeDeductible.filter((d) => d.id !== 0.02);

const riskType = FarmRiskType.Fo6Unscheduled;

const UnscheduledFarmProperty = observer(({ disabled, fieldName, riskIndex, riskIdString }) => {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const { inPolicyRoute } = useRouteHelper();

  const riskFieldName = `${fieldName}.risks[${riskIndex}]`;
  const { disableForm, setActiveForm } = useActiveFormState(riskFieldName);

  const disableFormFinal = Boolean(disableForm || disabled);

  const address = getIn(values, `${fieldName}.address`);

  const handleRemoveRisk = () => {
    const locationRisks = getIn(values, `${fieldName}.risks`);
    const newRisks = locationRisks.filter((_, index) => index !== riskIndex);

    setFieldValue(`${fieldName}.risks`, newRisks);
    setFieldTouched(`${fieldName}.risks`);
    setActiveForm(null);
  };

  return (
    <CollapsibleCard
      content={
        <div className={classes.header}>
          <div className={classes.headerRow}>
            <Label type="infoTitleSmall">FO-6 Unscheduled farm property</Label>
          </div>
        </div>
      }
      contentRight={
        <Field id={fieldName} name={fieldName} type="remove" disabled={disableForm} onClick={handleRemoveRisk} />
      }
      initialOpen={true}
      id={riskIdString}
    >
      <Grid container spacing={2} className={classes.greenCoverageCard}>
        <Grid item xs={12}>
          <Label type="formSubTitle">Coverages</Label>
        </Grid>

        <Field
          xs={6}
          id={`${fieldName}.risks[${riskIndex}].coverages.coverageG`}
          name={`${fieldName}.risks[${riskIndex}].coverages.coverageG`}
          type="numeric"
          mode="dark"
          label="Blanket Limit (G)"
          format={{ thousandSeparator: true, prefix: '$' }}
          disabled={disableFormFinal}
        />

        <Field
          xs={6}
          id={`${fieldName}.risks[${riskIndex}].coverages.deductible`}
          name={`${fieldName}.risks[${riskIndex}].coverages.deductible`}
          type="select"
          options={lookupsJson.farm.deductibleAllOther.filter((d) => d.id !== 500)}
          mode="dark"
          label="Blanket deductible"
          disabled={disableFormFinal}
        />

        <BrandFormField
          xs={6}
          id={`${fieldName}.risks[${riskIndex}].coverages.deductibleEarthquake`}
          name={`${fieldName}.risks[${riskIndex}].coverages.deductibleEarthquake`}
          type="select"
          options={eqDeductibleOptions}
          mode="dark"
          label="Earthquake Deductible"
          disabled={disableFormFinal}
          riskType={riskType}
          state={address.state}
        />

        <Field
          xs={6}
          name={`${fieldName}.risks[${riskIndex}].coverages.hasSnowWeightCoverage`}
          id={`${fieldName}.risks[${riskIndex}].coverages.hasSnowWeightCoverage`}
          type="checkbox"
          label="Weight of ice, snow, or sleet coverage"
          mode="dark"
          disabled={disableFormFinal}
          className={classes.snowWeightCoverage}
        />

        {/* Farm Income Coverage */}
        <Grid item xs={12}>
          <FarmIncomeCoverageForm
            fieldName={`${fieldName}.risks[${riskIndex}].coverages.scheduleCoverages`}
            disabled={disableFormFinal}
          />
        </Grid>
      </Grid>
    </CollapsibleCard>
  );
});

UnscheduledFarmProperty.propTypes = {
  disabled: PropTypes.bool,
  fieldName: PropTypes.string.isRequired,
  riskIndex: PropTypes.number.isRequired
};

UnscheduledFarmProperty.defaultProps = {
  disabled: false
};

export default UnscheduledFarmProperty;
